import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    AbstractControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import {
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { IMaskModule } from 'angular-imask';
import { Subject, takeUntil, tap } from 'rxjs';
import { IMaskConstants } from '../../../../../../core/constants/imask';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { RegExPattern } from '../../../../../../core/models/regexp-pattern/regex-pattern';
import { RegexInputDirective } from '../../../../../../shared/directives/regex-input/regex-input.directive';
import { ProductResponse } from '../../../shared/models/product-response';
import { ProductService } from '../../../shared/services/product/product.service';
import { OrderDetailsConstants } from '../../constants/order-details-constants';
import { OrderLineItemConstants } from '../../constants/order-line-item-constants';
import { OrderLineItemMaskConfig } from '../../constants/order-line-items-mask-config';
import { OrderLineItemFormModel } from '../../models/order-line-item-form-model';
import { OrderLineItemFormService } from '../../services/order-line-item-form.service';

@Component({
    selector: 'app-order-line-item-form-modal',
    templateUrl: './order-line-item-form-modal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatCardModule,
        MatButtonModule,
        MatDialogContent,
        MatSelectModule,
        MatDialogActions,
        MatDialogTitle,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
        MatChipsModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        RegexInputDirective,
        IMaskModule,
    ],
})
export class OrderLineItemFormModalComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    maskOptions = IMaskConstants;
    regExPattern = RegExPattern;
    orderDetailsConstants = OrderDetailsConstants;
    orderLineItemConstants = OrderLineItemConstants;
    orderLineItemMaskConfig = OrderLineItemMaskConfig;

    form: FormGroup<FormGroupType<OrderLineItemFormModel>> =
        this.orderLineItemFormService.getFormDefinition();

    productOptions: ProductResponse[] = [];
    availableProductDropdownOptions: DropdownOption[] = [];
    filteredProductDropdownOptions: DropdownOption[] = [];

    constructor(
        private readonly productService: ProductService,
        private readonly orderLineItemFormService: OrderLineItemFormService,
        private readonly dialogRef: MatDialogRef<OrderLineItemFormModalComponent>,
    ) {}

    ngOnInit(): void {
        this.loadDropdownOptions();
        this.setupFormListeners();
        this.setupFormValidators();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Handles the save action for the order line item form.
     */
    onSave(): void {
        if (this.form.valid) {
            this.dialogRef.close(this.form.value);
            return;
        }

        this.form.markAllAsTouched();
    }

    /**
     * Handles the cancel action for the order line item form.
     */
    onCancel(): void {
        this.dialogRef.close();
    }

    /**
     * Handles selection changes in the product dropdown. If there is a matching product option for the searched title,
     * then the product ID and price form controls are updated accordingly. If no match is found, both are set to null.
     *
     * @param title The selected product title.
     */
    onProductTitleSearchChange(title: string | null): void {
        const matchingProduct = this.productOptions.find(
            (productOption) =>
                productOption.title.toLowerCase() ===
                title?.trim().toLowerCase(),
        );

        // Autocomplete shows product titles for UX, but the API requires product IDs.
        // We sync the ID based on the selected title and auto-populate the price.
        // Both fields are cleared when no match exists to prevent invalid data.
        if (matchingProduct) {
            this.form.controls.productId.patchValue(matchingProduct.id);
            this.form.controls.price.patchValue(
                matchingProduct.rate.toFixed(2),
            );
            this.form.controls.quantity.reset(
                OrderLineItemConstants.INITIAL_QUANTITY_VALUE,
            );
        } else {
            this.form.controls.productId.patchValue(null);
            this.form.controls.price.patchValue(null);
        }

        // Mark the product ID control as touched to trigger validation highlighting if necessary.
        this.form.controls.productId.markAsTouched();
    }

    /**
     * Filters and sorts the product options based on the provided title.
     * If the title is null or empty, all options are shown.
     *
     * @param title The string to filter by.
     */
    filterProductOptions(title: string | null): void {
        let filteredProductDropdownOptions =
            this.availableProductDropdownOptions;

        if (title) {
            const terms = title
                .trim()
                .toLowerCase()
                .split(/\s+/)
                .filter(Boolean);

            filteredProductDropdownOptions =
                filteredProductDropdownOptions.filter(
                    (option: DropdownOption) => {
                        const label = option.label.trim().toLowerCase();
                        return terms.every((term) => label.includes(term));
                    },
                );
        }

        this.filteredProductDropdownOptions = filteredProductDropdownOptions;
    }

    /**
     * Sets up custom validators for the form controls.
     */
    private setupFormValidators(): void {
        this.form.controls.productName.addValidators(
            this.productNameAndIdValidator(),
        );
    }

    /**
     * Custom validator to ensure that if the product name is entered, the product ID must be valid.
     *
     * @return A ValidatorFn that checks the validity of the product ID based on the product name.
     */
    private productNameAndIdValidator(): ValidatorFn {
        return (_: AbstractControl): ValidationErrors | null => {
            const productIdControl = this.form.controls.productId;
            if (
                productIdControl &&
                productIdControl.invalid &&
                productIdControl.touched
            ) {
                return { productIdInvalid: true };
            }

            return null;
        };
    }

    /**
     * Sets up listeners for form value changes.
     */
    private setupFormListeners(): void {
        // When the product name changes, filter the product options and update the product ID accordingly.
        this.form.controls.productName.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap((title: string | null) => {
                    // Filter the product options based on the new title.
                    this.filterProductOptions(title);

                    // Update the product ID based on the new title (if it matches any product).
                    this.onProductTitleSearchChange(title);
                }),
            )
            .subscribe();

        // When the product ID status changes, update the validity of the product name control.
        this.form.controls.productId.statusChanges
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    // Update the control's validity without emitting another event to avoid circular updates.
                    this.form.controls.productName.updateValueAndValidity({
                        emitEvent: false,
                    });
                }),
            )
            .subscribe();
    }

    /**
     * Sets up the dropdown options for the form.
     */
    private loadDropdownOptions(): void {
        this.productService
            .getAll()
            .pipe(
                takeUntil(this.destroy$),
                tap((productOptions: ProductResponse[]) => {
                    this.productOptions = productOptions;
                    this.availableProductDropdownOptions =
                        this.transformProductsToDropdownOptions(productOptions);
                    this.filteredProductDropdownOptions =
                        this.availableProductDropdownOptions;
                }),
            )
            .subscribe();
    }

    /**
     * Transforms a list of products into dropdown options.
     *
     * @param productOptions The list of products to transform.
     * @returns An array of dropdown options.
     */
    private transformProductsToDropdownOptions(
        productOptions: ProductResponse[],
    ): DropdownOption[] {
        return productOptions.map((product: ProductResponse) => ({
            value: product.id,
            label: product.title,
        }));
    }
}
